import { useState, type ReactNode } from 'react';
import { Menu } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { STRINGS } from '@/strings/strings';
import { useAuthStore } from '@/stores/authStore';

/**
 * P5-4 Commit A + Commit B — the book flow's Kopfleiste, with an
 * optional Schrittleiste slot.
 *
 * Bau-Spez §5, Kopfleiste row:
 *   sticky top-0 z-40 bg-dream-navy
 *   paddingTop: env(safe-area-inset-top, 0)
 *   first row: flex items-center justify-between py-3
 *   content in <ContentContainer> default variant
 *   KEIN border-b (stroke-on-dark is verboten in the book flow)
 *   KEINE fixed height, neither h-* nor min-h-* — height composes
 *   from content padding.
 *
 * Composition mirrors `pwa/src/components/shell/TopBar.tsx:38-75`
 * WITHOUT modifying that file (Order R2: no touch to shell files).
 * Two stacked ContentContainers inside one sticky navy `<header>`:
 * first row = Kopfleiste (brand + settings), second row = optional
 * Schrittleiste slot. Both containers cap to 1100 px per §2.
 *
 * The brand mark is REPRODUCED inline from `TopBar.tsx:45` per
 * P5-4 Commit A Step 7 answer — the mark is inline JSX, not an
 * extractable component. Reproduction is one line, identical
 * src / alt / classes.
 *
 * The account affordance is auth-branched (Sebastian ruling
 * 2026-09-09 — Task 2 of the login/logout wave). Symmetric text
 * affordances — one click either way:
 *
 *   Anonymous  → text link "Anmelden" navigating to
 *                `/auth/login?returnTo=<current URL>` so the
 *                customer returns exactly where they were on
 *                successful re-login.
 *   Logged in  → text link "Abmelden" firing `authStore.logout()`
 *                and navigating to `/auth/login` with NO returnTo
 *                (logout = "I'm done for now"; a resume expectation
 *                would be wrong on this affordance). Matches the
 *                audio-side pattern at `AccountRoute.tsx:200-203`.
 *
 * ═══ TASK 3 COMMIT A — BOTH-WORLDS SWITCHER (2026-09-09) ═══════════
 *
 * A direct text link "Hörgeschichten" (renamed from
 * "Zu den Geschichten" 2026-09-24 for consistent product naming)
 * sits LEFT of the Anmelden/Abmelden affordance, rendered ONLY when
 * the customer has BOTH worlds (`user.hasBookWorld &&
 * user.hasAudioWorld`). Click navigates to `/library`.
 *
 * Label is the DESTINATION, not the current world (Sebastian
 * ruling 2026-09-09 reversing his earlier "carries the current
 * world as its label" framing: a button reading "Bücher" that
 * leads away from books is mislabelled, and the book PWA already
 * says where you are through the Schrittleiste).
 *
 * Anonymous customers (`user === null`) don't see the switcher —
 * hasBookWorld/hasAudioWorld are unknown until /me lands. Same for
 * a logged-in customer with only ONE world: switcher hides,
 * Anmelden/Abmelden alone renders.
 *
 * ═══ 2026-09-24 MOBILE MENU (below md breakpoint) ══════════════════
 *
 * Sebastian ruling 2026-09-24 — on mobile the three-to-four link
 * bar (Meine Bücher, Hörgeschichten, Abmelden, plus the newly-added
 * Passwort ändern from the retired footer link) wraps and crowds
 * the logo. Below `md` (Tailwind 768 px) the link bar is replaced
 * by a hamburger icon button (aria-label "Menü") that opens a
 * Radix `Popover`. Menu items, in this fixed order:
 *
 *   1. Meine Bücher
 *   2. Hörgeschichten   (only if both worlds — same guard as the
 *                        desktop link)
 *   3. Passwort ändern
 *   4. Abmelden
 *
 * Anonymous customers don't get a hamburger: their only affordance
 * is the Anmelden text link, which fits fine on mobile. The mobile
 * fallback mirrors the desktop anonymous view.
 *
 * The menu closes on:
 *   - outside tap  (Radix `Popover` default)
 *   - Escape        (Radix `Popover` default)
 *   - navigation    (`setMenuOpen(false)` fires BEFORE `navigate` in
 *                    every menu-item handler — mirrors the audio-side
 *                    `TopBar.tsx:82-100` precedent)
 *
 * Safe area at the top: unchanged. The header's outer
 * `paddingTop: env(safe-area-inset-top, 0)` covers both the visible
 * bar and (via the sticky context) any menu overlay that pushes
 * against the top of the viewport. Radix renders the popover in a
 * portal below the trigger, so it does not overlap the notch.
 *
 * ═══ DESKTOP LINK BAR (md and up) ═════════════════════════════════
 *
 * Desktop keeps every link visible, matching the pre-2026-09-24
 * shape plus two changes:
 *   - "Zu den Geschichten" renamed to "Hörgeschichten" (same target
 *     `/library`).
 *   - "Passwort ändern" added (target `/account/password`). Rationale:
 *     for book-only customers `OnboardingGuard.ONBOARDING_BYPASS_PATHS`
 *     lets them reach /account/password directly, but the pre-slice
 *     BookFooter was their SOLE header/footer entry point. Moving
 *     the affordance out of the footer without a desktop equivalent
 *     would leave a book-only desktop customer no way to change
 *     their password from inside the book flow.
 *
 * The `schrittleiste` slot is the P5-4 Commit B addition. When
 * omitted (as on the bare `/book` placeholder route) the second
 * ContentContainer row is not rendered — the header collapses back
 * to Kopfleiste-only. BookShell fills the slot when the URL matches
 * `/book/:orderId` and `useReentryStep` yields a derived step.
 *
 * The slot is a `ReactNode` rather than a `step` prop so this file
 * stays domain-neutral — the Schrittleiste component itself lives
 * next to its data source in the book feature folder.
 */

export interface BookHeaderProps {
  readonly schrittleiste?: ReactNode;
}

export function BookHeader({ schrittleiste }: BookHeaderProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [menuOpen, setMenuOpen] = useState(false);

  // Anonymous surface: link to /auth/login with the current URL as
  // returnTo so re-login lands the customer exactly where they left
  // off (matches ProtectedRoute.tsx:9-10 pattern for the returnTo
  // shape).
  const handleAnonymousLogin = () => {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    navigate(`/auth/login?returnTo=${returnTo}`);
  };

  // Logout: authStore.logout clears the token pair + user + PostHog
  // identity (authStore.ts:122-136), then navigate to /auth/login
  // with NO returnTo. Mirrors AccountRoute.tsx:200-203 verbatim.
  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/auth/login');
  };

  // Task 3 Commit A (Sebastian ruling 2026-09-09) — both-worlds
  // switcher. Renders iff BOTH signals are true. Anonymous users
  // fail the null-check on user; single-world users fail one of the
  // two boolean checks.
  const showBothWorldsSwitcher = user !== null && user.hasBookWorld && user.hasAudioWorld;

  // Sebastian ruling 2026-09-15 — Meine Bücher link. Rendered for
  // every authenticated customer, independent of hasBookWorld: a
  // customer with zero orders reaches the empty state via this
  // link. Suppressed on /book/meine itself so the current page's
  // header does not link to itself.
  const showMeineBuecherLink = user !== null && location.pathname !== '/book/meine';

  const isAuthenticated = user !== null;

  // Menu-item handlers close the popover BEFORE firing navigate so
  // the outgoing screen never sees a half-open menu overlay. Same
  // precedent as `TopBar.tsx:82-100`.
  const handleMenuGoToMeineBuecher = () => {
    setMenuOpen(false);
    navigate('/book/meine');
  };
  const handleMenuGoToLibrary = () => {
    setMenuOpen(false);
    navigate('/library');
  };
  const handleMenuGoToPassword = () => {
    setMenuOpen(false);
    navigate('/account/password');
  };

  return (
    <header
      className="sticky top-0 z-40 bg-dream-navy"
      style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}
    >
      <ContentContainer>
        <div className="flex items-center justify-between py-3">
          {/*
            Brand mark — reproduced inline from
            pwa/src/components/shell/TopBar.tsx:45 per P5-4 Step 7
            answer. TopBar.tsx is out of scope for this touch (Order
            R2 + guard tightening).
          */}
          <img src="/logo-dreamable.svg" alt="Dreamable" className="h-[18.656px] w-20 shrink-0" />

          {/*
            Mobile affordance — below md breakpoint (Sebastian ruling
            2026-09-24). Anonymous customers still see the Anmelden
            text link (fits fine, no crowding). Authenticated
            customers see the hamburger icon which opens the menu.
          */}
          <div className="flex items-center md:hidden">
            {isAuthenticated ? (
              <Popover open={menuOpen} onOpenChange={setMenuOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label={STRINGS.book.header.menuAriaLabel}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    className="text-dream-white transition-colors hover:text-dream-white/80"
                  >
                    <Menu className="h-6 w-6" strokeWidth={2} />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={8} className="w-56 p-1">
                  {showMeineBuecherLink && (
                    <BookHeaderMenuItem
                      label={STRINGS.book.header.meineBuecherLink}
                      onClick={handleMenuGoToMeineBuecher}
                    />
                  )}
                  {showBothWorldsSwitcher && (
                    <BookHeaderMenuItem
                      label={STRINGS.book.header.switcherToAudio}
                      onClick={handleMenuGoToLibrary}
                    />
                  )}
                  <BookHeaderMenuItem
                    label={STRINGS.book.header.changePasswordLink}
                    onClick={handleMenuGoToPassword}
                  />
                  <BookHeaderMenuItem
                    label={STRINGS.book.header.logoutLink}
                    onClick={handleLogout}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <button
                type="button"
                onClick={handleAnonymousLogin}
                className="text-sm font-medium text-dream-white underline underline-offset-2 transition-colors hover:text-dream-white/80"
              >
                {STRINGS.book.header.anonymousLoginLink}
              </button>
            )}
          </div>

          {/*
            Desktop link bar — md and up. Every visible link + the
            Anmelden/Abmelden affordance, in the same order as the
            pre-2026-09-24 shape, plus the renamed "Hörgeschichten"
            and the newly-added "Passwort ändern" for authenticated
            customers.
          */}
          <div className="hidden items-center gap-4 md:flex">
            {showMeineBuecherLink && (
              <button
                type="button"
                onClick={() => navigate('/book/meine')}
                className="text-sm font-medium text-dream-white underline underline-offset-2 transition-colors hover:text-dream-white/80"
              >
                {STRINGS.book.header.meineBuecherLink}
              </button>
            )}
            {showBothWorldsSwitcher && (
              <button
                type="button"
                onClick={() => navigate('/library')}
                className="text-sm font-medium text-dream-white underline underline-offset-2 transition-colors hover:text-dream-white/80"
              >
                {STRINGS.book.header.switcherToAudio}
              </button>
            )}
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => navigate('/account/password')}
                className="text-sm font-medium text-dream-white underline underline-offset-2 transition-colors hover:text-dream-white/80"
              >
                {STRINGS.book.header.changePasswordLink}
              </button>
            )}
            <button
              type="button"
              onClick={user === null ? handleAnonymousLogin : handleLogout}
              className="text-sm font-medium text-dream-white underline underline-offset-2 transition-colors hover:text-dream-white/80"
            >
              {user === null
                ? STRINGS.book.header.anonymousLoginLink
                : STRINGS.book.header.logoutLink}
            </button>
          </div>
        </div>
      </ContentContainer>
      {schrittleiste && <ContentContainer>{schrittleiste}</ContentContainer>}
    </header>
  );
}

interface BookHeaderMenuItemProps {
  readonly label: string;
  readonly onClick: () => void;
}

function BookHeaderMenuItem({ label, onClick }: BookHeaderMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
    >
      <span>{label}</span>
    </button>
  );
}
